import classNames from "classnames";
import React from "react";
import { t } from "ttag";

import { signupForEmailOffers } from "../../../api/user";
import { FormInput } from "../../../forms";
import { Form } from "../../../forms/Form";
import { FormSubmit } from "../../../forms/FormSubmit";

import styles from "./EmailCaptureContent.module.scss";

interface IEmailModal {
    title: string;
    content: string;
    confirm_content: string;
    image: string;
    footnotes?: string;
}

export interface IEmailModalSnippet {
    email_snippet: IEmailModal;
}

const buildFootnotes = (
    footnotes: string | undefined,
    formSubmitted: boolean,
): React.ReactNode => {
    const footnotesStyles = classNames({
        [styles.footnotes]: true,
        [styles.footnotesSubmitted]: formSubmitted,
    });

    if (!footnotes) {
        return;
    }

    return (
        <div
            className={footnotesStyles}
            dangerouslySetInnerHTML={{
                __html: footnotes,
            }}
        ></div>
    );
};

export const EmailCaptureContent = (props: {
    content: IEmailModalSnippet;
    onSubmit?: (height?: number) => void;
}): React.ReactNode => {
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [formSubmitted, setFormSubmitted] = React.useState(false);
    const [email, setEmail] = React.useState("");
    const contentElemRef = React.useRef<HTMLElement | null>(null);

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsSubmitting(true);
        try {
            await signupForEmailOffers(email);

            setIsSubmitting(false);
            setFormSubmitted(true);
            if (props.onSubmit && contentElemRef.current) {
                // If submit function exists, pass new height of content up to parent
                props.onSubmit(
                    contentElemRef.current.getBoundingClientRect().height,
                );
            }
        } catch {
            setIsSubmitting(false);
        }
    };

    const handleEmailChange = async (
        event: React.FormEvent<HTMLInputElement>,
    ) => {
        const newEmail = event.currentTarget.value;
        setEmail(newEmail);
    };

    const contentString: string = formSubmitted
        ? props.content.email_snippet.confirm_content
        : props.content.email_snippet.content;

    return (
        <>
            <section className={styles.copySection} ref={contentElemRef}>
                <div
                    className={`${styles.copy} rich-text`}
                    dangerouslySetInnerHTML={{ __html: contentString }}
                    role="status"
                ></div>
                {!formSubmitted && (
                    <div className={styles.form}>
                        <Form onSubmit={handleSubmit}>
                            <FormInput
                                id="email_modal_email"
                                name="email_modal_email"
                                type="email"
                                label={t`Email address`}
                                labelPlacement="dynamic"
                                required={true}
                                autoComplete="email"
                                value={email}
                                onChange={handleEmailChange}
                                disabled={isSubmitting}
                            />
                            <FormSubmit type="submit" value={t`Submit`} />
                        </Form>
                    </div>
                )}
                {buildFootnotes(
                    props.content.email_snippet.footnotes,
                    formSubmitted,
                )}
            </section>
        </>
    );
};
